import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { underwritingService } from "@/services/underwriting";
import { UnderwriterAssessmentFormData } from "@/types/underwriting";

export function useCreateAssessmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UnderwriterAssessmentFormData) => underwritingService.createAssessment(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assessment(data.id) });
      const borrowerId =
        typeof data.borrower === "number"
          ? data.borrower
          : data.borrower && typeof data.borrower === "object" && "id" in data.borrower
            ? (data.borrower as { id: number }).id
            : null;
      if (borrowerId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.assessmentByBorrower(borrowerId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
        queryClient.invalidateQueries({ queryKey: ["docusignForm", borrowerId] });
        // Contracts tab prefills offer terms from this cache; without invalidating
        // it here, it keeps showing stale terms after an offer is created/edited
        // until the user refreshes. Use the borrower-only prefix so it matches
        // every assessmentId/documentId variant cached for this borrower.
        queryClient.invalidateQueries({ queryKey: ["contractForm", borrowerId] });
        // No specific contract kind here, so invalidate the whole
        // contractStatus prefix to cover every kind cached for this borrower.
        queryClient.invalidateQueries({ queryKey: ["contractStatus", borrowerId] });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.borrowers() });
    },
  });
}

export function useUpdateAssessmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UnderwriterAssessmentFormData }) =>
      underwritingService.updateAssessment(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assessment(updated.id) });
      const borrowerId =
        typeof updated.borrower === "number"
          ? updated.borrower
          : updated.borrower && typeof updated.borrower === "object" && "id" in updated.borrower
            ? (updated.borrower as { id: number }).id
            : null;
      if (borrowerId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.assessmentByBorrower(borrowerId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
        queryClient.invalidateQueries({ queryKey: ["docusignForm", borrowerId] });
        // See comment in useCreateAssessmentMutation: keep Contracts' prefilled
        // offer terms in sync after an edit instead of showing stale values.
        queryClient.invalidateQueries({ queryKey: ["contractForm", borrowerId] });
        // See comment in useCreateAssessmentMutation for why this uses the
        // raw prefix instead of queryKeys.contractStatus.
        queryClient.invalidateQueries({ queryKey: ["contractStatus", borrowerId] });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.borrowers() });
    },
  });
}
